/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TServicesParams } from "@tivocorp/data-domain-lib";

import { DeviceModel } from "./device";
import type { ApplicationFeatureArea } from "./enums";
import { WhinyStateProvider } from "./logging/WhinyStateProvider";

/**
 * Common HTTP header values shared across all service API calls.
 * Mirrors the Kaveri CommonHeaders pattern: a single source of truth
 * for application and device metadata, with applicationFeatureArea
 * varying per call site.
 *
 * Covers the Standard and Common request headers from the api-policies doc,
 * grouped below by the doc's value-type taxonomy. The REQUEST-SCOPED headers
 * (`Origin-RequestId`, `x-amzn-requestid`) are deliberately absent: they are
 * generated per logical operation and are supplied per call via
 * TServicesParamsOverrides, not baked into this constructor-built set.
 * The Authenticated request headers (`Authorization`, `BodyId`) are likewise
 * absent - the doc assigns those to a separate Auth interceptor.
 *
 * @see https://github.com/tivocorp/unity-client-doc/blob/main/doc/api-policies/common-headers.md
 */
export interface ICommonHeaders {
  /** STATIC - HTTP client config. Recommend gzip. */
  "Accept-Encoding": string;
  /** RUNTIME - user locale, RFC 7231 list. Server default: en-US. */
  "Accept-Language": string;
  /** UI-DRIVEN - supplied by the UI/feature layer; never fabricated here. */
  ApplicationFeatureArea: ApplicationFeatureArea;
  /** STATIC - unique reverse-domain app id, e.g. com.xperi.bravo. */
  ApplicationName: string;
  /** STATIC - {versionName}-{versionCode}. */
  ApplicationVersion: string;
  /** PER-REQUEST - media type of the payload. */
  "Content-Type": string;
  /** DEVICE-SPECIFIC - model of the device making the request. */
  DeviceModel: string;
  /** DEVICE-SPECIFIC - see the DeviceType Enum Values section of the doc. */
  DeviceType: string;
  /** DEVICE-SPECIFIC - platform firmware/build version for the device. */
  FirmwareVersion: string;
  /** DEVICE-SPECIFIC - OS version of the device making the request. */
  OsVersion: string;
  /** STATIC - must be unique to that application. */
  ProductName: string;
  /**
   * RUNTIME - the doc specifies an Olson timezone name for the body, but this
   * client deliberately sends the app-wide "GMT" convention (see
   * TvProperties.getDefaultTimeZone). Reconcile with the doc before changing.
   */
  TimeZoneName: string;
}

/**
 * Per-request overrides for building TServicesParams from CommonHeaders.
 * Derived from TServicesParams to avoid field duplication.
 * basePath is required; all other fields are optional per-request values.
 */
export type TServicesParamsOverrides = Pick<
  TServicesParams,
  | "basePath"
  | "accessToken"
  | "bodyId"
  | "profileId"
  | "timeZoneName"
  | "xAmznRequestId"
>;

/**
 * Centralized source of truth for common application and device
 * request metadata. Reads runtime values from DeviceModel so that
 * service consumers never hardcode app name, version, device type,
 * or product name.
 *
 * Usage:
 * ```ts
 * const common = new CommonHeaders(ApplicationFeatureArea.LOGIN);
 * const params = common.toServicesParams({ basePath: endpoint });
 * ```
 */
export class CommonHeaders {
  public readonly headers: ICommonHeaders;

  constructor(applicationFeatureArea: ApplicationFeatureArea) {
    this.headers = {
      "Accept-Encoding": "gzip",
      "Accept-Language":
        DeviceModel.getTVProperties().getDateLocale() || "en-US",
      ApplicationFeatureArea: applicationFeatureArea,
      ApplicationName: DeviceModel.getAppName(),
      ApplicationVersion: DeviceModel.getAppVersionName(),
      "Content-Type": "application/json",
      DeviceModel: DeviceModel.getTVProperties().getDeviceModel(),
      DeviceType: DeviceModel.getTiVoSettings().getDeviceType(),
      FirmwareVersion: DeviceModel.getFirmwareVersion(),
      OsVersion: DeviceModel.getTVProperties().getOSVersion(),
      ProductName: DeviceModel.getAppName(),
      // Deliberately the app-wide "GMT" convention, not the device's local
      // zone: services return time data in GMT and the Date constructor
      // converts it locally. Matches what baseInteractor already passes.
      TimeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone()
    };
  }

  /**
   * Derives a TServicesParams object from the common headers plus
   * per-request overrides such as basePath, accessToken, etc.
   *
   * `profileId` defaults to the one held in TivoSettings, but callers that
   * resolved a token for a specific context should override it with that
   * context — the `Authorization` and `profileId` headers must describe the
   * same one, and TivoSettings can lag mid-switch.
   *
   * @param overrides - Per-request values (basePath is required)
   * @returns A fully populated TServicesParams
   */
  public toServicesParams(
    overrides: TServicesParamsOverrides
  ): TServicesParams {
    const profileId: string =
      overrides.profileId !== undefined
        ? overrides.profileId
        : DeviceModel.getTiVoSettings().getProfileId();
    const params: TServicesParams = {
      basePath: overrides.basePath,
      acceptLanguage: this.headers["Accept-Language"],
      applicationFeatureArea: this.headers.ApplicationFeatureArea,
      applicationName: this.headers.ApplicationName,
      applicationVersion: this.headers.ApplicationVersion,
      deviceModel: this.headers.DeviceModel,
      deviceType: this.headers.DeviceType,
      firmwareVersion: this.headers.FirmwareVersion || "",
      osVersion: this.headers.OsVersion,
      productName: this.headers.ProductName,
      timeZoneName: this.headers.TimeZoneName,
      whiny: WhinyStateProvider.isWhinyEnabledNow() ? "true" : "false"
    };

    if (profileId.length > 0) {
      params.profileId = profileId;
    }
    if (overrides.accessToken !== undefined) {
      params.accessToken = overrides.accessToken;
    }
    if (overrides.bodyId !== undefined) {
      params.bodyId = overrides.bodyId;
    }
    if (overrides.timeZoneName !== undefined) {
      params.timeZoneName = overrides.timeZoneName;
    }
    if (overrides.xAmznRequestId !== undefined) {
      params.xAmznRequestId = overrides.xAmznRequestId;
    }

    return params;
  }
}
